"use client";

import React from "react";
import { useTranslations } from "@/lib/i18n-client";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/motion";
import { Database, CalendarRange, Share2, MessageCircle, ShieldCheck, Headset } from "lucide-react";

/**
 * Coastal Freedom — "Smarter Connectivity, Better Travel": a big coastal
 * lifestyle photo + a 6-item benefit grid framed around vacation data adequacy,
 * trip-length plans, hotspot sharing and transparent fair-use (the brand's
 * trust wedge vs throttled "unlimited" — research/05 + 07 CF3). NOT generic
 * coverage/security filler (anti-converge). Client / claim-safe.
 */
export default function WhyTravel() {
  const { t } = useTranslations();

  const benefits = [
    { icon: Database, title: t("cf.why.b1", "Trip-sized data"), body: t("cf.why.b1b", "Large-data plans sized for maps, social, video and uploads — enough for the whole trip.") },
    { icon: CalendarRange, title: t("cf.why.b2", "Pick your validity"), body: t("cf.why.b2b", "Weekend Escape to Big Trip — choose the data and days that fit your vacation.") },
    { icon: ShieldCheck, title: t("cf.why.b3", "Transparent fair-use"), body: t("cf.why.b3b", "High-data plans show clear fair-use terms — no fine-print 'unlimited' surprises.") },
    { icon: Share2, title: t("cf.why.b4", "Hotspot for the family"), body: t("cf.why.b4b", "Share your connection with travel companions — tethering is supported.") },
    { icon: MessageCircle, title: t("cf.why.b5", "Keep your number"), body: t("cf.why.b5b", "Data-only eSIM runs alongside your home SIM — stay reachable on WhatsApp and SMS.") },
    { icon: Headset, title: t("cf.why.b6", "24/7 support"), body: t("cf.why.b6b", "Real people, anytime you need help on your journey.") },
  ];

  return (
    <section className="container mx-auto px-4 py-10">
      <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-8 items-center">
        <Reveal as="div" y={24} className="cf-photo cf-photo-wash aspect-[4/3] rounded-2xl">
          <span className="sr-only">{t("cf.why.title2", "Better Travel")}</span>
        </Reveal>
        <div>
          <div className="text-xs font-bold text-primary uppercase tracking-wide mb-2">{t("cf.why.eyebrow", "Why travelers choose us")}</div>
          <h2 className="text-2xl sm:text-3xl mb-2">{t("cf.why.title1", "Smarter Connectivity,")} <span className="cf-accent">{t("cf.why.title2", "Better Travel")}</span></h2>
          <p className="text-muted-foreground mb-6 max-w-lg">{t("cf.why.sub", "Built for vacation travelers who want enough data to share, stream and explore — stress-free, with no roaming bill shock.")}</p>
          <Stagger as="div" className="grid sm:grid-cols-2 gap-x-6 gap-y-5" staggerChildren={0.1} amount={0.2}>
            {benefits.map(({ icon: Icon, title, body }) => (
              <StaggerItem key={title} y={24} className="flex items-start gap-3">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary shrink-0 transition-transform duration-300 group-hover:scale-110"><Icon className="w-5 h-5" /></span>
                <div><div className="font-bold text-foreground text-sm">{title}</div><div className="text-xs text-muted-foreground">{body}</div></div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </div>
    </section>
  );
}
